import discountQueries from '@apis/queries/discountQueries';
import { DiscountCouponRow } from '@models/discount';
import { OrderLine } from '@models/order';
import useCalculatedRequest from '@store/order/hooks/useCalculatedRequest';
import { useQuery } from '@tanstack/react-query';
import { isItemCouponApplicableLine } from '@utils/couponUtils';
import { makeItemKey } from '@utils/orderUtils';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';

import { ItemCouponTarget, itemCouponDraftAtom } from '../atoms';

/** 적용할 상품 모달의 선택지 하나 */
export interface ItemCouponOption {
    /** 선택지 식별자. 일반 쿠폰은 상품 단위(itemKey#unit), 1+1 쿠폰은 상품 라인(itemKey) */
    key: string;
    line: OrderLine;
    target: ItemCouponTarget;
    /** 다른 쿠폰이 적용되어 고를 수 없음 */
    disabled: boolean;
}

export interface ItemCouponState {
    coupon: DiscountCouponRow;
    /** 쿠폰 조건상 고를 수 있는 선택지 (다른 쿠폰이 적용된 것 포함) */
    options: ItemCouponOption[];
    /** 그중 지금 고를 수 있는 선택지 */
    candidates: ItemCouponOption[];
    /** 초안에서 이 쿠폰을 적용한 대상 */
    assigned?: ItemCouponOption;
}

/** 초안의 적용 대상을 선택지 식별자로 */
export function toOptionKey(coupon: DiscountCouponRow, target: ItemCouponTarget) {
    return coupon.discountMethod === 'BOGO' ? target.itemKey : `${target.itemKey}#${target.units[0]}`;
}

/**
 * 상품 쿠폰 목록과 쿠폰별 적용 대상.
 * 같은 상품을 여러 개 담았으면 일반 쿠폰은 상품 1개씩 따로 고르고, 1+1 쿠폰은 남은 상품 2개를 차지한다.
 * 다른 쿠폰이 적용된 상품에는 쿠폰을 더 적용할 수 없다.
 */
function useItemCoupons() {
    const { shopId, orderLines } = useCalculatedRequest();
    const { data } = useQuery({
        ...discountQueries.list(shopId),
        enabled: !!shopId,
    });
    const draft = useAtomValue(itemCouponDraftAtom);

    const items = useMemo<ItemCouponState[]>(() => {
        const coupons = data?.coupons.filter((it) => it.discountTarget === 'ITEM') || [];
        // 다른 쿠폰이 차지한 상품 단위
        const takenUnits = (itemKey: string, couponId: string) => new Set(Object.entries(draft)
            .filter(([otherId, target]) => target.itemKey === itemKey && otherId !== couponId)
            .map(([, target]) => target.units)
            .flat());

        const result = coupons.map((coupon) => {
            const eligibleLines = orderLines.filter((line) => isItemCouponApplicableLine(coupon, line));
            const options: ItemCouponOption[] = eligibleLines.map((line) => {
                const itemKey = makeItemKey(line);
                const taken = takenUnits(itemKey, coupon.id);
                const units = Array.from({ length: line.quantity }, (_, unit) => unit);
                if (coupon.discountMethod === 'BOGO') {
                    const freeUnits = units.filter((unit) => !taken.has(unit));
                    return [{
                        key: itemKey,
                        line,
                        target: { itemKey, units: freeUnits.slice(0, 2) },
                        disabled: freeUnits.length < 2,
                    }];
                }
                return units.map((unit) => ({
                    key: `${itemKey}#${unit}`,
                    line,
                    target: { itemKey, units: [unit] },
                    disabled: taken.has(unit),
                }));
            }).flat();
            const assignedTarget = draft[coupon.id];
            const assigned = assignedTarget
                ? options.find((option) => option.key === toOptionKey(coupon, assignedTarget))
                : undefined;
            return {
                coupon,
                options,
                candidates: options.filter((option) => !option.disabled),
                assigned: assigned && { ...assigned, target: assignedTarget },
            };
        });
        // 사용할 수 없는 쿠폰은 맨 아래로
        return [
            ...result.filter((it) => it.assigned || it.candidates.length > 0),
            ...result.filter((it) => !it.assigned && it.candidates.length === 0),
        ];
    }, [data, orderLines, draft]);

    return items;
}

export default useItemCoupons;
